import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

type DropdownMenuItemProps = {
    id: string;
    label: string;
    icon?: React.ReactNode;
    className?: string;
    onClick?: () => void;
};

export function DropdownMenu({
    items,
    selectedOptionId,
    className,
    placeholder = "Select...",
}: {
    items: DropdownMenuItemProps[];
    selectedOptionId: string;
    className?: string;
    placeholder?: string;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [isPositioned, setIsPositioned] = useState(false);

    const toggleMenu = () => {
        if (isOpen) {
            setIsPositioned(false);
        }
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setMenuPosition({
                top: rect.bottom + 4,
                left: rect.left,
            });
            setIsPositioned(true);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setIsPositioned(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div className="flex shrink-0 flex-col gap-2">
            <div className="relative">
                <div
                    ref={buttonRef}
                    onClick={toggleMenu}
                    className={clsx(
                        "flex cursor-pointer items-center gap-1",
                        "subdued transition-colors hover:text-gray-300",
                        className || "body-micro",
                    )}
                >
                    <span>
                        {selectedOptionId
                            ? items.find((item) => item.id === selectedOptionId)
                                  ?.label
                            : items[0]?.label || placeholder}
                    </span>
                    <ChevronDownIcon
                        className={clsx(
                            "size-3 transition-transform duration-200",
                            isOpen && "rotate-180",
                        )}
                    />
                </div>
                {isOpen && (
                    <div
                        ref={menuRef}
                        style={{
                            position: "fixed",
                            top: `${menuPosition.top}px`,
                            left: `${menuPosition.left}px`,
                            opacity: isPositioned ? 1 : 0,
                        }}
                        className={clsx(
                            "bg-slate-800",
                            "rounded-lg shadow-400",
                            "custom-scrollbar max-h-[400px] overflow-auto p-2",
                            "z-50",
                            "min-w-[160px]",
                            "flex flex-col gap-1",
                        )}
                    >
                        {items.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => {
                                    item.onClick?.();
                                    setIsOpen(false);
                                }}
                                className={clsx(
                                    "flex h-[36px] w-full items-center gap-2 rounded-md px-2 py-1.5",
                                    "menu-item body-micro cursor-pointer hover:bg-slate-600",
                                    selectedOptionId === item.id &&
                                        "bg-selected",
                                )}
                                role="menuitem"
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
